import { memo, useCallback, useContext, useEffect, useRef, useState } from "react";
import { MapContainer, useMapEvents } from "react-leaflet";
import ReactLeafletGoogleLayer from "react-leaflet-google-layer";
import { toast } from "react-toastify";
import SelectArea from "leaflet-area-select";
import "leaflet/dist/leaflet.css";
import { useSearchParams } from "react-router-dom";
import MapFilters from "./common/MapFilterButtons";
import FloatingActionMenu from "../../components/FloatingActionMenu";
import useLeafletMap from "./hooks/useLeafletMap";
import L from "../../helper/CustomLeaflet";
import FloatingInfoMenu from "../../components/common/FloatingMenu";
import MapCitySearch from "../../components/MapCitySearch";
import BigBrotherDocs from "../../components/BigBrotherDocs";
import MapIsTypingContext from "context/MapIsTypingContext";
import { FocusMapDuotoneIcon, MergeOrderDuotoneIcon, ResetViewportDuotoneIcon, AreaCoverageDuotoneIcon, AssignOrdersDuotoneIcon, UnassignOrderDuotoneIcon, ShowRouteDuotoneIcon, ThemeShiftIcon, DocsIcon, MergePublishOrderDuotoneIcon } from "components/svg";
import { zoom, centerLat, centerLng, point1Lat, point1Lng, point2Lat, point2Lng } from "constants/map";
import { MapStyleRetro, MapStyleRoad } from "constants/enums";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";

const MAP_CONTAINER_STYLE = {
  height: "calc(98vh - 64px)",
  position: "relative",
};

function MapEventBridge({ onMapReady }) {
  const map = useMapEvents({});

  useEffect(() => {
    if (map) {
      onMapReady(map);
    }
  }, [map, onMapReady]);

  return null;
}

const OrdersMapContainer = memo(function OrdersMapContainer({
  initialCenter,
  initialZoom,
  mapTheme,
  onMapCreated,
  onMapReady,
}) {
  const handleMapCreated = useCallback(
    (map) => {
      onMapCreated(map);
    },
    [onMapCreated]
  );

  return (
    <MapContainer
      center={initialCenter}
      zoom={initialZoom}
      zoomControl={true}
      tap={false}
      attributionControl={false}
      style={MAP_CONTAINER_STYLE}
      whenCreated={handleMapCreated}
    >
      <ReactLeafletGoogleLayer
        key={mapTheme}
        apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
        type="roadmap"
        styles={mapTheme === "BarqLite" ? MapStyleRetro : MapStyleRoad}
      />
      <MapEventBridge onMapReady={onMapReady} />
    </MapContainer>
  );
});

export default function LeafletMap({
  handleAssign,
  handleUnassign,
  mergeOrdersToShipment,
  shipments,
  drivers,
  selectedShipmentIds,
  setSelectedShipmentIds,
  selectedShipments,
  selectedDriverId,
  selectedDriver,
  setSelectedDriverId,
  setSelectedDriver,
  filters,
  setFilters,
  handleMapBoundsChange,
  areaSelectRegion,
  setAreaSelectRegion,
  handleQueryChange
}) {
  const {
    map,
    setMap,
    routeList,
    showShipmentMarkers,
    clearDropoffMarkers,
    showDriverMarkers,
    showShipmentListRoute,
    selectMarkersWithinRegion,
    fitMapToBounds,
    removeShipmentRouteLayers,
    handleShowDropoffs,
    controlDropoffMode,
    isDropoffMode,
    dropoffMarkers,
    limitMessage,
    setLimitMessage,
    clusterSelection,
    closeClusterSelection,
    handleShipmentSelect,
    isShipmentSelected,
  } = useLeafletMap({
    selectedShipmentIds,
    setSelectedShipmentIds,
    selectedDriverId,
    setSelectedDriverId,
    setSelectedDriver,
    filters,
    drivers,
    shipments,
    selectedShipments,
  });
  const localStorageFilters = JSON.parse(
    window?.localStorage?.getItem("mapPageFilters") || "{}"
  );
  const [searchParams, setSearchParams] = useSearchParams(
    localStorageFilters || {}
  );
  const mapRef = useRef(null);
  const initialViewRef = useRef(null);
  if (!initialViewRef.current) {
    const lat = parseFloat(searchParams.get("mapCanterLat"));
    const lng = parseFloat(searchParams.get("mapCanterLng"));
    const mapZoom = parseFloat(searchParams.get("zoom"));
    initialViewRef.current = {
      center: [
        Number.isFinite(lat) ? lat : centerLat,
        Number.isFinite(lng) ? lng : centerLng,
      ],
      zoom: Number.isFinite(mapZoom) ? mapZoom : zoom,
    };
  }

  const handleMapCreated = useCallback((mapInstance) => {
    mapRef.current = mapInstance;
  }, []);

  const handleMapReady = useCallback(
    (mapInstance) => {
      setMap((currentMap) =>
        currentMap === mapInstance ? currentMap : mapInstance
      );
    },
    [setMap]
  );

  const { isInputFocused } = useContext(MapIsTypingContext);
  const [areaCoverage, setAreaCoverage] = useState(false)
  const [showDirection, setShowDirection] = useState(false)
  const [mapTheme, setMapTheme] = useState(searchParams.get("mapTheme") || "BarqLite");

  const fitMapViewToSelectedElements = () => {
    let coordinates = [];

    if (filters.shipment.showOnMap) {
      selectedShipments.map((order) =>
        coordinates.push({
          latitude: order.origin.latitude,
          longitude: order.origin.longitude,
        })
      );
    }

    if (filters.driver.showOnMap) {
      if (selectedDriverId) {
        coordinates.push({
          latitude: selectedDriver.latitude,
          longitude: selectedDriver.longitude,
        });
      }
    }

    if (filters.dropoff.showOnMap) {
      selectedShipments.forEach((order) => {
        coordinates.push({
          latitude: order.destination.latitude,
          longitude: order.destination.longitude,
        });
      });
    }

    fitMapToBounds(map, coordinates);
  };

  useEffect(() => {
    if (limitMessage) {
      toast.error(limitMessage);
      setLimitMessage("");
    }
  }, [limitMessage]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isInputFocused && e.altKey) {
        switch (e.key.toLowerCase()) {
          case "r":
            showSelectedShipmentsRouteOnMap();
            e.preventDefault();
            break;
          case "g":
            toggleAreaCoverage();
            e.preventDefault();
            break;
          case "a":
            handleAssign();
            e.preventDefault();
            break;
          case "u":
            handleUnassign();
            e.preventDefault();
            break;
          case "f":
            fitMapViewToSelectedElements();
            e.preventDefault();
            break;
          case "m":
            mergeOrdersToShipment(false);
            e.preventDefault();
            break;
          case "p":
            mergeOrdersToShipment(true);
            e.preventDefault();
            break;
        }
      }
    };

    document.addEventListener("keydown", handleKeyPress);

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [
    selectedShipmentIds,
    selectedDriverId,
    routeList,
    isInputFocused,
    dropoffMarkers,
  ]);

  // Adding tile: Google maps
  useEffect(() => {
    window.L.tileLayer("http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}", {
      maxZoom: 20,
      subdomains: ["mt0", "mt1", "mt2", "mt3"],
    });
  }, []);

  // Check whether map has loaded
  useEffect(() => {
    if (map) {
      const closeOnDragStart = () => {
        closeClusterSelection();
      };
      map.on("dragstart", closeOnDragStart);

      // enable are select SHIFT+CTRL+left click
      map.selectArea.enable();
      map.on("areaselected", (e) => {
        const selectedArea = L.rectangle(e.bounds);
        setAreaSelectRegion(selectedArea);
      });

      map.on(
        "moveend zoomend",
        (function () {
          if (areaSelectRegion) map.removeLayer(areaSelectRegion);

          let timer;
          return function () {
            clearTimeout(timer);
            timer = setTimeout(function () {
              handleMapBoundsChange(
                map.getBounds().getNorthEast().lat,
                map.getBounds().getNorthEast().lng,
                map.getBounds().getSouthWest().lat,
                map.getBounds().getSouthWest().lng,
                map.getBounds().getCenter(),
                map.getZoom());
            }, 500);
          };
        })()
      );

      return () => {
        map.off("dragstart", closeOnDragStart);
      };
    }
  }, [map]);

  useEffect(() => {
    if (areaSelectRegion) {
      selectMarkersWithinRegion(areaSelectRegion);
    }
  }, [areaSelectRegion]);

  // add shipment markers to the map
  useEffect(() => {
    showShipmentMarkers();
  }, [shipments, selectedShipmentIds, map]);

  useEffect(() => {
    // clearDropoffMarkers()
    if (isDropoffMode) handleShowDropoffs();
  }, [selectedShipmentIds, selectedShipments, shipments, map]);

  useEffect(() => {
    removeShipmentRouteLayers();
    setShowDirection(false);
  }, [selectedShipmentIds]);

  useEffect(() => {
    showDriverMarkers();
  }, [filters.driver.showOnMap]);

  useEffect(() => {
    showShipmentMarkers();
  }, [filters.shipment.showOnMap]);

  useEffect(() => {
    controlDropoffMode(filters.dropoff.showOnMap);
  }, [filters.dropoff.showOnMap]);

  // add driver markers to the map
  useEffect(() => {
    showDriverMarkers();
  }, [drivers, selectedDriverId, map]);

  useEffect(() => {
    if (filters.driver.apply == true) {
      showDriverMarkers();
    }
  }, [filters.driver.apply]);

  const showSelectedShipmentsRouteOnMap = () => {
    // remove unselected routes
    removeShipmentRouteLayers();

    if (map && selectedShipments?.length > 0) {
      setShowDirection(prev => !prev);
      showShipmentListRoute(selectedShipments, areaCoverage);
    } else {
      toast.error("Select at least one order to show the route");
    }
    // prevent map being undraggable
    if (map) {
      map.dragging.enable();
    }
  };

  const toggleAreaCoverage = () => {
    if (showDirection) {
      removeShipmentRouteLayers();
      setAreaCoverage(prev => !prev);
    } else {
      toast.error("Kindly enable the route before proceeding.");
    }
  };

  useEffect(() => {
    if (map && selectedShipments?.length > 0) {
      if (showDirection) {
        showShipmentListRoute(selectedShipments, areaCoverage);
      }
    }
  }, [areaCoverage]);

  // add selected driver to the map
  useEffect(() => {
    if (selectedDriverId) {
      const selectedDriver = drivers?.find(
        (driver) => driver.id === selectedDriverId
      );
    }
    // prevent map being undraggable
    if (map) {
      map.dragging.enable();
    }
  }, [selectedDriverId]);

  const floatingMenuActions = [
    {
      icon: <ResetViewportDuotoneIcon />,
      name: "Reset Viewport",
      handleClick: () => {
        handleQueryChange({
          mapCanterLat: centerLat,
          mapCanterLng: centerLng,
          zoom: zoom,
          point1Lat: point1Lat,
          point1Long: point1Lng,
          point2Lat: point2Lat,
          point2Long: point2Lng,
        });
        handleMapBoundsChange(
          point1Lat,
          point1Lng,
          point2Lat,
          point2Lng,
          { lat: centerLat, lng: centerLng },
          zoom
        );
        mapRef.current.setView([centerLat, centerLng], zoom);
      },
    },
    {
      icon: <FocusMapDuotoneIcon />,
      name: "Focus selected (alt + f)",
      handleClick: () => fitMapViewToSelectedElements(),
    },
    {
      icon: <ShowRouteDuotoneIcon fill={showDirection == false ? '#BDBDBD' : '#666'} />,
      name: "Show Route (alt + r)",
      handleClick: () => showSelectedShipmentsRouteOnMap(),
    },
    {
      icon: <AreaCoverageDuotoneIcon fill={areaCoverage == false ? '#bdbabd5e' : '#666'} />,
      name: "Area Coverage (alt + g)",
      handleClick: () => toggleAreaCoverage(),
    },
    {
      icon: <AssignOrdersDuotoneIcon />,
      name: "Assign (alt + a)",
      handleClick: () => handleAssign(),
    },
    {
      icon: <UnassignOrderDuotoneIcon />,
      name: "UnAssign (alt + u)",
      handleClick: () => handleUnassign(),
    },
    {
      icon: <MergeOrderDuotoneIcon />,
      name: "Merge (alt + m)",
      handleClick: () => mergeOrdersToShipment(false),
    },
    {
      icon: <MergePublishOrderDuotoneIcon />,
      name: "Merge & Publish (alt + p)",
      handleClick: () => mergeOrdersToShipment(true),
    },
  ];

  const handleCitySelect = (city) => {
    if (!city || !city.centerLat || !city.centerLng) {
      toast.error("Something went wrong.");
      return;
    }

    const zoomLevel = 12;

    handleQueryChange({
      mapCanterLat: city.centerLat,
      mapCanterLng: city.centerLng,
      zoom: zoomLevel,
      point1Lat: city.point1Lat,
      point1Long: city.point1Lng,
      point2Lat: city.point2Lat,
      point2Long: city.point2Lng,
    });

    handleMapBoundsChange(
      city.point1Lat,
      city.point1Lng,
      city.point2Lat,
      city.point2Lng,
      { lat: city.centerLat, lng: city.centerLng },
      zoomLevel
    );

    mapRef.current.setView([city.centerLat, city.centerLng], zoomLevel);
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = (theme) => {
    setMapTheme(theme);
    handleQueryChange({ mapTheme: theme });
    handleClose();
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className="max-h-screen w-full flex-1 z-0" style={{ height: "98vh" }}>
      <div className="flex-1 bg-white shadow-lg bg-light px-1" style={{
        height: "45px",
        position: "relative",
        display: "flex",
        alignItems: "center",
        marginTop: "10px",
        marginBottom: "9px",
        borderRadius: "8px",
        justifyContent: "space-between"
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
        }}>
          <MapFilters filters={filters} setFilters={setFilters} />
          <div className="-ml-2 "><FloatingActionMenu actions={floatingMenuActions} /></div>
        </div>
        <div style={{
          display: "flex",
          alignItems: "center",
        }}>
          <div className="mr-2"><MapCitySearch onSelect={handleCitySelect} /></div>
          <FloatingInfoMenu actions={floatingMenuActions} />
        </div>
      </div>
      <div className="relative">
        <OrdersMapContainer
          initialCenter={initialViewRef.current.center}
          initialZoom={initialViewRef.current.zoom}
          mapTheme={mapTheme}
          onMapCreated={handleMapCreated}
          onMapReady={handleMapReady}
        />
        <div className="absolute top-[80px] right-[12px] z-[1000]">
          <div
            onClick={handleClick}
            className="bg-[#ffffff] p-[5px] rounded-t-[4px] border-[2px] border-b-[0px] border-[#c2beb3] shadow-[#c2beb3]"
          >
            <ThemeShiftIcon />
          </div>
          <BigBrotherDocs />
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            MenuListProps={{
              sx: {
                padding: 0,
              },
            }}
            PaperProps={{
              sx: {
                padding: 0,
              },
            }}
          >
            <MenuItem onClick={() => handleMenuItemClick("Roadmap")} disabled={mapTheme === "Roadmap"}>
              Roadmap
            </MenuItem>
            <MenuItem onClick={() => handleMenuItemClick("BarqLite")} disabled={mapTheme === "BarqLite"}>
              Barqlite
            </MenuItem>
          </Menu>
        </div>
      </div>
      <Popover
        open={clusterSelection.open}
        onClose={() => {}}
        disableEscapeKeyDown
        sx={{ pointerEvents: "none" }}
        PaperProps={{ sx: { pointerEvents: "auto" } }}
        anchorReference="anchorPosition"
        anchorPosition={clusterSelection.position || undefined}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box
          sx={{
            width: 200,
            height: 300,
            display: "flex",
            flexDirection: "column",
            p: 0.75,
            gap: 0.4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
              Group Orders
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Button size="small" variant="text" sx={{ minWidth: 0, px: 0.75, py: 0 }} onClick={closeClusterSelection}>
                Close
              </Button>
            </Box>
          </Box>
          <Divider />
          <Box sx={{ overflowY: "auto", pr: 0.25 }}>
            {clusterSelection.shipmentIds.map((id) => (
              <Box
                key={id}
                sx={{
                  py: 0,
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600, lineHeight: 1.1 }}>
                  #{id}
                </Typography>
                <Checkbox
                  size="small"
                  sx={{ p: 0.25 }}
                  checked={isShipmentSelected(id)}
                  onChange={() => handleShipmentSelect(id)}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Popover>
    </div>
  );
}